/**
 * Protected Route Component
 * Wrapper component that checks authentication and redirects to login if not authenticated.
 * Redirects unprovisioned users (no role assigned) to /unauthorized.
 */

import React, { useEffect, useRef } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/app/contexts/AuthContext';
import { hasPermission, isProvisioned } from '@/app/utils/permissions';
import { auditService } from '@/app/services';
import { logger } from '@/app/utils/logger';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: string;
  requiredPermission?: string;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requiredRole,
  requiredPermission 
}) => {
  const { isAuthenticated, isLoading, userProfile } = useAuth();
  const location = useLocation();
  const hasLoggedDenial = useRef(false);

  // Determine denial reasons
  const isUnprovisioned = isAuthenticated && !isProvisioned(userProfile);
  const hasRoleDenial = requiredRole && userProfile?.role !== requiredRole;
  const hasPermissionDenial = requiredPermission && !hasPermission(userProfile, requiredPermission);

  // Log access denied events (only once per mount)
  useEffect(() => {
    if (hasLoggedDenial.current) return;
    
    if (isUnprovisioned) {
      hasLoggedDenial.current = true;
      auditService.logAccessDenied(
        'access_protected_route',
        'provisioned_role',
        'route',
        undefined,
        location.pathname
      ).catch((err) => logger.warn('Failed to log access denied', { error: err }));
    } else if (hasRoleDenial) {
      hasLoggedDenial.current = true;
      auditService.logAccessDenied(
        'access_protected_route',
        `role:${requiredRole}`,
        'route',
        undefined,
        location.pathname
      ).catch((err) => logger.warn('Failed to log access denied', { error: err }));
    } else if (hasPermissionDenial) {
      hasLoggedDenial.current = true;
      auditService.logAccessDenied(
        'access_protected_route',
        requiredPermission!,
        'route',
        undefined,
        location.pathname
      ).catch((err) => logger.warn('Failed to log access denied', { error: err }));
    }
  }, [isUnprovisioned, hasRoleDenial, hasPermissionDenial, requiredRole, requiredPermission, location.pathname]);

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <div>Loading...</div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Redirect to unauthorized if authenticated but no role assigned (unprovisioned user)
  if (isUnprovisioned) {
    return <Navigate to="/unauthorized" replace />;
  }

  // Check role requirement if specified
  if (hasRoleDenial) {
    return (
      <div style={{ padding: '2rem', color: 'var(--color-text-primary)' }}>
        <h2>Access Denied</h2>
        <p>You do not have the required role to access this page.</p>
      </div>
    );
  }

  // Check permission requirement if specified
  if (hasPermissionDenial) {
    return (
      <div style={{ padding: '2rem', color: 'var(--color-text-primary)' }}>
        <h2>Access Denied</h2>
        <p>You do not have the required permission to access this page.</p>
        <p style={{ marginTop: '1rem', fontSize: '0.9rem', opacity: 0.7 }}>
          Required permission: {requiredPermission}
        </p>
      </div>
    );
  }

  // Render children if authenticated and authorized
  return <>{children}</>;
};

export default ProtectedRoute;

